import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Table, Button, notification } from 'antd'
import moment from 'moment'
import downloadFileFromData from '../../../../helpers/downloadFileFromData'
import { bulkMergeBatches } from '../../../../services/api/3dApi'

const escapeCsvCell = (value) => {
    if (value === undefined || value === null) return ''
    const str = String(value)
    if (/[",\r\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`
    }
    return str
}

const BulkCreatedEmbroideryBatchesModal = ({ visible, onClose, data = {} }) => {
    const [processing, setProcessing] = useState(false)
    const [processError, setProcessError] = useState('')

    const errors = data.errors || []

    // Tổng số item lỗi per (batch_name, sku) — chỉ tính errors có failed_items
    const failedCountMap = {}
    errors.forEach(e => {
        if (e.batch_name && Array.isArray(e.failed_items) && e.failed_items.length > 0) {
            const key = `${e.batch_name}__${e.sku}`
            failedCountMap[key] = (failedCountMap[key] || 0) + e.failed_items.length
        }
    })

    // Trừ failed count một lần duy nhất trên dòng đầu của mỗi (batch_name, sku)
    const appliedKeys = new Set()
    const rows = (data.rows || []).map((r, i) => {
        const key = `${r.batch_name}__${r.sku}`
        const failedCount = failedCountMap[key] || 0
        if (failedCount > 0 && !appliedKeys.has(key)) {
            appliedKeys.add(key)
            return { ...r, quantity: Math.max(0, r.quantity - failedCount), key: i }
        }
        return { ...r, key: i }
    }).sort((a, b) => a.batch_name.localeCompare(b.batch_name) || a.sku.localeCompare(b.sku) || a.size.localeCompare(b.size))

    const buildRowSpanMap = (rows, keyFn) =>
        rows.reduce((acc, row, index) => {
            if (index === 0 || keyFn(rows[index - 1]) !== keyFn(row)) {
                let span = 1
                for (let j = index + 1; j < rows.length; j++) {
                    if (keyFn(rows[j]) === keyFn(row)) span++
                    else break
                }
                acc[index] = span
            } else {
                acc[index] = 0
            }
            return acc
        }, {})

    const batchRowSpanMap = buildRowSpanMap(rows, r => r.batch_name)
    const skuRowSpanMap = buildRowSpanMap(rows, r => `${r.batch_name}__${r.sku}`)

    const columns = [
        {
            title: 'Tên lô',
            dataIndex: 'batch_name',
            render: (text, _, index) => ({ children: text, props: { rowSpan: batchRowSpanMap[index] } }),
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            render: (text, _, index) => ({ children: text, props: { rowSpan: skuRowSpanMap[index] } }),
        },
        { title: 'Size', dataIndex: 'size' },
        { title: 'Số lượng', dataIndex: 'quantity' },
    ]

    const _handleProcessProductionFiles = async () => {
        const batchIds = [...new Set((data.rows || []).map(r => r.batch_id).filter(Boolean))]
        if (!batchIds.length) {
            setProcessError('Chưa có thông tin batch_id. Vui lòng liên hệ kỹ thuật.')
            return
        }
        setProcessing(true)
        setProcessError('')
        try {
            const { data: response } = await bulkMergeBatches({ list_batch_id: batchIds, need_convert_cmyk: false })
            const { success, data: resData, error } = response || {}
            if (!success) throw new Error(error || 'Xử lý thất bại')
            const { successJobs = [], failed = [] } = resData || {}
            notification.open({
                message: 'Xử lý file sản xuất',
                description: (
                    <div>
                        Có <strong className='text-success'>{successJobs.length}</strong> lô bắt đầu quá trình xử
                        lý file, <strong className='text-danger'>{failed.length}</strong> lô đã chuyển trạng thái sản xuất.
                    </div>
                ),
                duration: 5,
            })
            onClose()
        } catch (e) {
            setProcessError(e.message || 'Có lỗi xảy ra')
        } finally {
            setProcessing(false)
        }
    }

    const _handleExportExcel = () => {
        if (!rows.length) return
        const header = ['Tên lô', 'SKU', 'Size', 'Số lượng']
        const csvLines = [
            header.map(escapeCsvCell).join(','),
            ...rows.map((r, i) => [
                batchRowSpanMap[i] === 0 ? '' : r.batch_name,
                skuRowSpanMap[i] === 0 ? '' : r.sku,
                r.size,
                r.quantity,
            ].map(escapeCsvCell).join(',')),
        ]
        const csv = csvLines.join('\r\n')
        const filename = `danh-sach-lo-theu-${moment().format('YYYY-MM-DD-HH-mm-ss')}.csv`
        downloadFileFromData(csv, { filename })
    }

    return (
        <Modal
            title='Danh sách tạo lô thêu'
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={800}
        >
            {rows.length > 0 ? (
                <>
                    <div className='d-flex justify-content-end mb-2'>
                        <Button type='primary' icon='download' onClick={_handleExportExcel} className='mr-2'>
                            Export
                        </Button>
                        <Button type='primary' icon='tool' loading={processing} onClick={_handleProcessProductionFiles}>
                            Xử lý file sản xuất
                        </Button>
                    </div>
                    {!!processError && (
                        <div className='text-danger mb-2' style={{ fontSize: '12px' }}>{processError}</div>
                    )}
                    <Table
                        dataSource={rows}
                        columns={columns}
                        pagination={false}
                        size='small'
                    />
                    {errors.some(e => Array.isArray(e.failed_items) && e.failed_items.length > 0) && (
                        <div className='text-warning mt-1' style={{ fontSize: '12px' }}>
                            * Số lượng đã điều chỉnh trừ item lỗi. Chi tiết xem phần lỗi bên dưới.
                        </div>
                    )}
                </>
            ) : (
                <div className='text-center py-3'>Không có lô nào được tạo thành công.</div>
            )}

            {errors.length > 0 && (
                <div className='mt-3'>
                    <strong className='text-danger'>Có {errors.length} lỗi:</strong>
                    <ul className='mb-0 mt-1'>
                        {errors.map((e, i) => (
                            <li key={i} className='text-danger'>
                                <span><strong>{e.sku}</strong>{e.batch_name ? ` (lô ${e.batch_name})` : ''}: {e.message}</span>
                                {Array.isArray(e.failed_items) && e.failed_items.length > 0 && (
                                    <ul className='mb-0'>
                                        {e.failed_items.map((item, j) => (
                                            <li key={j}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    )
}

BulkCreatedEmbroideryBatchesModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    data: PropTypes.object,
}

export default BulkCreatedEmbroideryBatchesModal
